import { autoSignIn, confirmSignUp, fetchUserAttributes, signIn } from "@aws-amplify/auth";
import { useCallback } from "react";
import SecureLS from "secure-ls";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/store/hooks";
import { setStylistInfo, setUser } from "@/store/slices/authSlice";
import { useCreateUserInfo } from "./useCreateUserInfo";

import { v4 as uuidv4 } from "uuid";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { createStylistInfo } from "@/graphql/mutations";
import { User, UserInfo } from "@/types/AppTypes";
const ls = new SecureLS({ encodingType: "aes" });

interface EmailVerificationOptions {
  autoNavigate?: boolean;
  onSuccess?: (user: any) => void;
  onError?: (error: any) => void;
}

const useEmailVerification = (
  otpRef: React.RefObject<any>,
  setLoading: (loading: boolean) => void,
  state?: any,
  options?: EmailVerificationOptions
) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { create, loading, error, data } = useCreateUserInfo();


  const { autoNavigate = true, onSuccess, onError } = options || state || {
    autoNavigate: true,
    onSuccess: () => {},
    onError: () => {},
  };

  // const handleFetchUserAttributes = useCallback(async () => {
  //   try {
  //     const userAttributes = await fetchUserAttributes();
  //     ls.set("user", JSON.stringify(userAttributes));
  //   } catch (error) {
  //     console.log("login::::: ", error);
  //   }
  // }, [dispatch]);

  const completeSignUp = useCallback(
    async (verificationCode: string) => {
      try {
        setLoading(true);

        // First API call - confirm signup
        const response = await confirmSignUp({
          username: state.email,
          confirmationCode: verificationCode,
        });

        // Only proceed if signup is actually complete
        if (!response.isSignUpComplete) {
          throw new Error("Sign up confirmation failed");
        }
        if(state?.role === "distributor") {
          if(onSuccess) {
            onSuccess({
              email: state.email,
            });
          }

          return;
        }


        if (autoNavigate) {
          toast.success("Account verified and signed in successfully");
        }

        let user: UserInfo = {} as UserInfo;

        if (state?.role === "buyer") {
          user = await create({
            id: uuidv4(),
            user_id: state.userId || state.cognitoUserId,
            email: state.email,
            first_name: state?.firstName || "",
            last_name: state?.lastName || "",
            has_roles: [state?.role],
            current_role: state?.role,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

           if(!user){
            toast.error("Failed to create user");
            return;
           }

          const stylistInfo = await executeGraphQLMutation({
            query: createStylistInfo,
            variables: {
              input: {
                id: uuidv4(),
                user_id: state.userId || state.cognitoUserId,
                saloon_name: state.salonName || "",
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                license_path: state?.license || "",
                is_verified: false,
              },
            },
          });

          if(!stylistInfo) {
            toast.error("Failed to create stylist info");
            return;
          }

          if (autoNavigate && user) {
            toast.success("Account created successfully",{
              description: "Please wait for admin approval",
              duration: 10000,
            },

          
          );
            navigate("/");
          }
        }

     

        return user || null;
      } catch (error: any) {
        console.error("Email verification error:", error);

        // Handle specific error types
        if (error?.name === "CodeMismatchException") {
          toast.error("Invalid or expired verification code");
        } else if (error?.name === "NotAuthorizedException") {
          toast.error("Invalid verification code");
        } else {
          toast.error("Verification failed", {
            description: error.message || "Please try again",
          });
        }

        otpRef.current?.resetOtp();

        if (onError) {
          onError(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      state.email,
      //handleFetchUserAttributes,
      otpRef,
      autoNavigate,
      onSuccess,
      onError,
      navigate,
      create,
      dispatch,
    ]
  );

  return {
    completeSignUp,
  };
};

export default useEmailVerification;
